import { acquireSegmentation, getSegmentedTrack } from './segmentation-source'
import type { SegmentationFrame, SegmentationHandle } from './segmentation-source'
import { vclog } from '../hooks/vc-log'

/**
 * Composites the camera against a background and produces a track to PUBLISH.
 *
 * This exists because the background used to be a local-only illusion:
 * `VirtualBackgroundStream` painted a cutout over a CSS background inside your
 * own tile, and `useLocalMedia` published the untouched camera track. You saw
 * your background; everyone else saw your room — and only you could not tell.
 *
 * Two differences from the display component, both forced by `captureStream`:
 *
 *  1. **The background is drawn into the canvas.** The display path leaves the
 *     canvas transparent and lets CSS paint the image underneath, which a
 *     captured stream cannot do.
 *  2. **The image is fetched with CORS.** Drawing a cross-origin image taints
 *     the canvas, and a tainted canvas cannot be captured — the track would
 *     exist and carry nothing. The bundled backgrounds are Unsplash URLs that
 *     serve `access-control-allow-origin: *`, so `crossOrigin = "anonymous"`
 *     is enough; a background host without CORS would silently break this.
 */

/** Output resolution. Segmentation cost scales with area, not camera size. */
const OUTPUT_WIDTH = 1280
const OUTPUT_HEIGHT = 720
const OUTPUT_FPS = 30
/** ~5s at 30fps. Re-checks that the mask still contains a person. */
const DIAGNOSTIC_INTERVAL_FRAMES = 150

export class VirtualBackgroundProcessor {
  private canvas: HTMLCanvasElement | null = null
  /** Segmentation is shared per camera track — see `segmentation-source.ts`. */
  private segmentation: SegmentationHandle | null = null
  private unsubscribe: (() => void) | null = null
  private backgroundImage: HTMLImageElement | null = null
  /** > 0 means blur the real background instead of replacing it. */
  private blurPx = 0
  private outputStream: MediaStream | null = null
  private rafId: number | null = null
  private running = false
  /** Resolves once a real composited frame exists, so callers never publish black. */
  private firstFrame: { promise: Promise<void>; resolve: () => void } | null = null
  private diagnosed = false
  private framesSinceCheck = 0
  private lastHadPerson: boolean | null = null

  /**
   * Start compositing `source` against `backgroundUrl`.
   *
   * Resolves only after the first composited frame has been drawn — publishing
   * the track before that would send a few frames of blank canvas, which looks
   * like a camera failure to everyone else.
   */
  async start(
    source: MediaStreamTrack,
    background: { url?: string; blurPx?: number },
  ): Promise<MediaStreamTrack> {
    this.stop()
    this.running = true

    let resolveFirst: () => void = () => {}
    this.firstFrame = {
      promise: new Promise<void>((resolve) => {
        resolveFirst = resolve
      }),
      resolve: resolveFirst,
    }

    const canvas = document.createElement('canvas')
    // Sized from the camera, not a constant. With a fixed 1280x720 the output
    // ignored the quality preset entirely, so choosing Low still published a
    // 720p canvas and choosing 1080p silently downscaled.
    const settings = source.getSettings?.() ?? {}
    canvas.width = settings.width ?? OUTPUT_WIDTH
    canvas.height = settings.height ?? OUTPUT_HEIGHT
    this.canvas = canvas

    this.blurPx = background.blurPx ?? 0
    // The image and the segmentation run start together. One after the other,
    // a full-size background download was paid before MediaPipe even began
    // loading — seconds of nothing after Apply.
    //
    // Shared: if the publish pipeline is already segmenting this camera, this
    // joins that run rather than starting a second one. Two concurrent
    // MediaPipe instances break each other's masks.
    const [image, segmentation] = await Promise.allSettled([
      background.url ? loadImage(background.url) : Promise.resolve(null),
      acquireSegmentation(source),
    ])
    if (segmentation.status === 'fulfilled') this.segmentation = segmentation.value
    if (image.status === 'rejected' || segmentation.status === 'rejected') {
      this.stop() // releases the segmentation claim if that half succeeded
      throw image.status === 'rejected' ? image.reason : (segmentation as PromiseRejectedResult).reason
    }
    this.backgroundImage = image.value
    this.unsubscribe = segmentation.value.subscribe((frame) => this.draw(frame))

    this.outputStream = canvas.captureStream(OUTPUT_FPS)
    const [track] = this.outputStream.getVideoTracks()
    if (!track) throw new Error('virtual background: canvas produced no track')

    // Bounded: a model that never loads or an image that never arrives used to
    // leave this pending forever — the camera never published, and a mid-meeting
    // change never finished. Failing lets the caller fall back to the raw camera.
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        this.firstFrame.promise,
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('virtual background: no composited frame in time')), START_TIMEOUT_MS)
        }),
      ])
    } catch (err) {
      this.stop()
      track.stop()
      throw err
    } finally {
      clearTimeout(timer)
    }
    return track
  }

  /** Swap image or blur without restarting segmentation or the published track. */
  async setBackground(background: { url?: string; blurPx?: number }): Promise<void> {
    this.blurPx = background.blurPx ?? 0
    this.backgroundImage = background.url ? await loadImage(background.url) : null
  }

  stop(): void {
    this.running = false
    this.unsubscribe?.()
    this.unsubscribe = null
    // Releases this consumer's claim. The shared run only ends when the last
    // consumer lets go, so closing the dialog cannot disturb what is publishing.
    this.segmentation?.release()
    this.segmentation = null
    this.outputStream?.getTracks().forEach((t) => t.stop())
    this.outputStream = null
    this.canvas = null
    this.backgroundImage = null
    this.firstFrame = null
  }

  private draw(results: SegmentationFrame): void {
    const canvas = this.canvas
    const ctx = canvas?.getContext('2d', { willReadFrequently: true })
    if (!canvas || !ctx || !this.running) return

    const w = canvas.width
    const h = canvas.height

    ctx.save()
    ctx.clearRect(0, 0, w, h)

    // Person first, masked to their outline, then the background painted only
    // where the person is not. Compositing this way round means the mask edge
    // is drawn once — layering background over a full frame and punching a hole
    // leaves a visible seam.
    ctx.drawImage(results.image, 0, 0, w, h)
    ctx.globalCompositeOperation = 'destination-in'
    ctx.drawImage(results.segmentationMask, 0, 0, w, h)

    // One-time check, before the background goes down: does the cutout contain
    // anyone? An all-transparent layer here means the mask came back empty and
    // what gets published is the background photo with nobody in it — which
    // looks like a working effect to the sender and an empty room to everyone
    // else. Safe to read: only camera pixels are on the canvas at this point,
    // and camera frames do not taint it.
    if (!this.diagnosed || this.framesSinceCheck++ >= DIAGNOSTIC_INTERVAL_FRAMES) {
      this.diagnosed = true
      this.framesSinceCheck = 0
      try {
        const sample = ctx.getImageData(0, 0, w, h)
        let opaque = 0
        // Every 400th pixel is plenty to tell "someone" from "nothing".
        for (let i = 3; i < sample.data.length; i += 4 * 400) {
          if (sample.data[i] > 8) opaque++
        }
        // Only worth saying when it CHANGES — otherwise this is noise every
        // few seconds for the whole call.
        const hasPerson = opaque > 0
        if (hasPerson !== this.lastHadPerson) {
          this.lastHadPerson = hasPerson
          vclog('producer', hasPerson ? 'virtual background: person detected' : 'virtual background: MASK EMPTY — publishing background only', {
            sampledOpaquePixels: opaque,
          })
        }
      } catch (err) {
        vclog('producer', 'virtual background: canvas is tainted, capture will be blank', {
          error: (err as Error).message,
        })
      }
    }

    ctx.globalCompositeOperation = 'destination-over'
    if (this.blurPx > 0) {
      // Blur the camera frame itself and put it behind the sharp cutout. This
      // is why blur belongs in the pipeline rather than in CSS: a CSS filter
      // over the video blurs the person too, which is the opposite of the
      // point. `ctx.filter` samples beyond the edges, so the frame is drawn
      // slightly oversized to keep the blur from bleeding in a soft border.
      const bleed = this.blurPx * 2
      ctx.filter = `blur(${this.blurPx}px)`
      ctx.drawImage(results.image, -bleed, -bleed, w + bleed * 2, h + bleed * 2)
      ctx.filter = 'none'
    } else if (this.backgroundImage) {
      drawCover(ctx, this.backgroundImage, w, h)
    } else {
      ctx.fillStyle = '#111'
      ctx.fillRect(0, 0, w, h)
    }

    ctx.restore()
    this.firstFrame?.resolve()
  }
}

/** `object-fit: cover` for a canvas — a stretched background reads as a bug. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  w: number,
  h: number,
): void {
  const scale = Math.max(w / img.width, h / img.height)
  const dw = img.width * scale
  const dh = img.height * scale
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh)
}

/** How long `start()` waits for its first composited frame before giving up. */
const START_TIMEOUT_MS = 20_000
/** How long a background image may take to load. */
const IMAGE_TIMEOUT_MS = 15_000

/** Decoded backgrounds by URL, so a preloaded or re-chosen image is not fetched again. */
const images = new Map<string, Promise<HTMLImageElement>>()

function loadImage(url: string): Promise<HTMLImageElement> {
  const cached = images.get(url)
  if (cached) return cached
  const loading = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    // Required: without it the canvas is tainted and cannot be captured.
    img.crossOrigin = 'anonymous'
    const timer = setTimeout(() => reject(new Error(`virtual background: ${url} took too long`)), IMAGE_TIMEOUT_MS)
    img.onload = () => { clearTimeout(timer); resolve(img) }
    img.onerror = () => { clearTimeout(timer); reject(new Error(`virtual background: cannot load ${url}`)) }
    img.src = url
  })
  images.set(url, loading)
  loading.catch(() => images.delete(url))
  return loading
}

/** Start fetching a background the user is looking at, so Apply finds it ready. */
export function preloadBackgroundImage(url: string | undefined): void {
  if (url) void loadImage(url).catch(() => {})
}

/**
 * Hold a segmentation run on `track` while the background dialog is open, so
 * Apply joins a run that is already warm instead of loading MediaPipe then.
 * Returns the release.
 */
export function warmSegmentation(track: MediaStreamTrack): () => void {
  let released = false
  let handle: { release: () => void } | null = null
  acquireSegmentation(track)
    .then((h) => {
      if (released) h.release()
      else handle = h
    })
    .catch(() => {})
  return () => {
    released = true
    handle?.release()
    handle = null
  }
}

/**
 * Mid-meeting background changes, for the screen to report. Emitted by the
 * publish pipeline; the meeting screen turns them into a status toast.
 */
export type BackgroundStatus = 'applying' | 'applied' | 'removed' | 'failed'
const statusListeners = new Set<(status: BackgroundStatus) => void>()
export function onBackgroundStatus(listener: (status: BackgroundStatus) => void): () => void {
  statusListeners.add(listener)
  return () => { statusListeners.delete(listener) }
}
export function emitBackgroundStatus(status: BackgroundStatus): void {
  for (const l of statusListeners) l(status)
}

/** Some browsers report readyState before any frame exists. */
function waitForFrames(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 && video.videoWidth > 0) return Promise.resolve()
  return new Promise((resolve) => {
    const settle = () => {
      video.removeEventListener('loadeddata', settle)
      video.removeEventListener('canplay', settle)
      resolve()
    }
    video.addEventListener('loadeddata', settle)
    video.addEventListener('canplay', settle)
  })
}

/**
 * The camera track the meeting is currently publishing from.
 *
 * The background dialog needs a camera to preview against. Opening its own with
 * `getUserMedia` gave it a SECOND capture of the same device, which cost a
 * second segmentation run and — because that capture came from the refcounted
 * MediaStreamContext — meant closing the dialog could drop the shared refcount
 * to zero and stop the camera the tiles were rendering, blacking out your own
 * video. Previewing the track that is already publishing avoids both.
 *
 * Module scope rather than a prop: the dialog sits three layers below
 * `useLocalMedia` and nothing in between has any use for a camera track.
 */
let publishedCameraTrack: MediaStreamTrack | null = null

export function setPublishedCameraTrack(track: MediaStreamTrack | null): void {
  publishedCameraTrack = track
}

/** The live published camera, or null if it has ended (camera off, or left). */
export function getPublishedCameraTrack(): MediaStreamTrack | null {
  if (publishedCameraTrack?.readyState === 'ended') publishedCameraTrack = null
  // Falls back to whatever is already being segmented. This variable is module
  // state, and module state is reset by a dev-server hot reload while the
  // effect that sets it does not re-run — returning null there sent the picker
  // off to open a SECOND camera, which is the whole bug. A run in progress is
  // direct evidence of the camera actually in use.
  return publishedCameraTrack ?? getSegmentedTrack()
}
